'use client';

import * as React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Card, 
  hasSeparatedCodes, 
  getHtmlCode, 
  getCssCode, 
  getJsCode,
  getHtmlInstructions,
  getCssInstructions,
  getJsInstructions,
  getCardCode
} from '../../lib/supabase';
import { incrementCodeCopyCount } from '../../lib/admin';
import { useAuth } from '../../hooks/useAuth';

interface CodeTabsProps {
  card: Card;
  onCopy?: (code: string, type: 'html' | 'css' | 'js' | 'code') => void;
  copying?: boolean;
}

export function CodeTabs({ card, onCopy, copying = false }: CodeTabsProps) {
  const [activeTab, setActiveTab] = React.useState<'html' | 'css' | 'js' | 'code'>('html');
  const [copyingType, setCopyingType] = React.useState<string | null>(null);
  const { user } = useAuth();

  const hasSeparated = hasSeparatedCodes(card.data);
  const htmlCode = getHtmlCode(card.data);
  const cssCode = getCssCode(card.data);
  const jsCode = getJsCode(card.data);
  const htmlInstructions = getHtmlInstructions(card.data);
  const cssInstructions = getCssInstructions(card.data);
  const jsInstructions = getJsInstructions(card.data);
  const legacyCode = getCardCode(card.data);

  // Determinar qual aba mostrar por padrão
  React.useEffect(() => {
    if (hasSeparated) {
      // Se tiver códigos separados, mostrar a primeira aba que tiver conteúdo
      if (htmlCode) setActiveTab('html');
      else if (cssCode) setActiveTab('css');
      else if (jsCode) setActiveTab('js');
      else setActiveTab('html');
    } else {
      setActiveTab('code');
    }
  }, [hasSeparated, htmlCode, cssCode, jsCode]);

  const handleCopy = async (code: string, type: 'html' | 'css' | 'js' | 'code') => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'code-tabs.tsx:53',message:'CodeTabs handleCopy iniciado',data:{hasCode:!!code,codeLength:code?.length,type,userId:user?.id},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H9',runId:'post-fix'})}).catch(()=>{});
    // #endregion
    if (!code || code.trim() === '') return;
    
    setCopyingType(type);
    try {
      await navigator.clipboard.writeText(code);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'code-tabs.tsx:58',message:'Código copiado no CodeTabs',data:{codeLength:code.length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H9',runId:'post-fix'})}).catch(()=>{});
      // #endregion
      
      // Rastrear cópia de forma assíncrona e não bloqueante
      if (user?.id) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/de71e05c-d9c0-4fcc-bf2e-a43ef0ae691a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'code-tabs.tsx:62',message:'Chamando incrementCodeCopyCount do CodeTabs',data:{userId:user.id},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H9',runId:'post-fix'})}).catch(()=>{});
        // #endregion
        incrementCodeCopyCount(user.id).catch(err => {
          console.error('Erro ao rastrear cópia:', err);
          // Erro silencioso - não afetar UX
        });
      }
      
      if (onCopy) {
        onCopy(code, type);
      }
    } catch (err) {
      console.error('Erro ao copiar:', err);
    } finally {
      setTimeout(() => setCopyingType(null), 2000);
    }
  };

  // Se não tiver códigos separados, mostrar apenas o código legado
  if (!hasSeparated) {
    return (
      <div className="w-full">
        <div className="border-b border-gray-200 dark:border-[#8a2be2]/30 mb-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white font-ubuntu">Código</h3>
            <Button
              onClick={() => handleCopy(legacyCode, 'code')}
              disabled={!legacyCode || copying}
              loading={copying && copyingType === 'code'}
              type="secondary"
              size="small"
              prefix={!copying || copyingType !== 'code' ? <i className="ri-file-copy-line"></i> : undefined}
            >
              {copying && copyingType === 'code' ? 'Copiando...' : 'Copiar'}
            </Button>
          </div>
        </div>
        <div className="bg-gray-100 dark:bg-[#0d0d0d] rounded-lg p-4 overflow-x-auto">
          <pre className="text-sm font-mono text-gray-800 dark:text-[#e0e1dd] whitespace-pre-wrap">
            {legacyCode ? String(legacyCode).replace(/<script[\s\S]*?<\/script>/gi, '') : '// Nenhum código disponível'}
          </pre>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'html' as const, label: 'HTML', icon: 'ri-html5-line', code: htmlCode, instructions: htmlInstructions },
    { id: 'css' as const, label: 'CSS', icon: 'ri-css3-line', code: cssCode, instructions: cssInstructions },
    { id: 'js' as const, label: 'JS', icon: 'ri-javascript-line', code: jsCode, instructions: jsInstructions },
  ];

  return (
    <div className="w-full">
      {/* Tabs Header */}
      <div className="border-b border-gray-200 dark:border-[#778da9]/30 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const hasContent = tab.code && tab.code.trim() !== '';
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'px-4 py-2 flex items-center space-x-2 rounded-t-lg transition-all duration-200 font-montserrat text-sm',
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-[#6a0dad] to-[#8a2be2] text-white'
                      : 'text-gray-600 dark:text-[#778da9] hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10'
                  )}
                >
                  <i className={tab.icon}></i>
                  <span>{tab.label}</span>
                  {!hasContent && (
                    <span className="text-xs opacity-70">(Opcional)</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {tabs.map((tab) => {
          if (activeTab !== tab.id) return null;
          
          const hasContent = tab.code && tab.code.trim() !== '';
          
          return (
            <motion.div
              key={tab.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {/* Instructions */}
              {tab.instructions && tab.instructions.trim() && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <i className="ri-information-line text-blue-600 dark:text-blue-400 mt-0.5"></i>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1 font-ubuntu">
                        Como usar ({tab.label}):
                      </h4>
                      <p className="text-sm text-blue-800 dark:text-blue-300 font-montserrat whitespace-pre-wrap">
                        {tab.instructions}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Code */}
              <div className="bg-gray-100 dark:bg-[#0d1b2a] rounded-lg p-4 overflow-x-auto relative">
                <div className="absolute top-4 right-4 z-10">
                  <Button
                    onClick={() => handleCopy(tab.code || '', tab.id)}
                    disabled={!hasContent || copying}
                    loading={copying && copyingType === tab.id}
                    type="secondary"
                    size="small"
                    className="bg-white/90 dark:bg-gradient-to-br dark:from-[#1a0f2b]/90 dark:to-[#2d1b3d]/90 backdrop-blur-sm"
                    prefix={!copying || copyingType !== tab.id ? <i className="ri-file-copy-line"></i> : undefined}
                  >
                    {copying && copyingType === tab.id ? 'Copiando...' : 'Copiar'}
                  </Button>
                </div>
                <pre className="text-sm font-mono text-gray-800 dark:text-[#e0e1dd] whitespace-pre-wrap pr-20">
                  {hasContent ? String(tab.code).replace(/<script[\s\S]*?<\/script>/gi, '') : `// ${tab.label} opcional`}
                </pre>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

